import { useContext, useEffect } from 'react';

import {
  CLIENT_PREVIEW_PARAM,
  CLIENT_PREVIEW_VALUE,
  STUDIO_CLIENT_PREVIEW_PARAM,
  STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM,
} from 'src/constants/studioFtux';
import { RouteContext } from 'src/context';
import { STUDIO_CHECKLIST_COMPLETE_PCT } from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import history from 'src/history';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setStudioClientPreview } from 'src/store/ui/actions';
import {
  selectIsStudioClientPreview,
  selectStudioClientPreviewProgress,
} from 'src/store/ui/selectors';

export interface StudioClientPreview {
  /** True while this session is a CRM "Open portal" client preview. */
  isPreview: boolean;
  /** Progress bar completion percentage (0-100) read off the preview link. */
  progress: number;
}

// Untrusted URL input: keep only a whole percentage within 0-100, so a tampered
// or malformed param can never over/under-fill the bar.
const parseProgress = (raw: string | undefined): number => {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed)) {
    return 0;
  }
  return Math.min(Math.max(parsed, 0), STUDIO_CHECKLIST_COMPLETE_PCT);
};

/**
 * Details of the current CRM "Open portal" client preview. The landing URL carries
 * the preview param for its workspace kind — `studioClientPreview` on Studio,
 * `clientPreview` on classic — plus, on Studio only, the "Getting started"
 * progress percentage.
 *
 * Both the preview flag and the progress percentage live in the (unpersisted)
 * store so every SidebarNav instance shares them — on mobile the temporary drawer
 * mounts a second one, and it survives that drawer remounting across route
 * transitions. Keeping progress local would let a later instance read `isPreview`
 * from the store while its own progress reset to 0 (the URL param is already
 * stripped), rendering an empty bar. Both params are stripped from the URL on
 * capture; a full refresh resets the store, so the preview ends there.
 */
export const useStudioClientPreview = (): StudioClientPreview => {
  const { query } = useContext(RouteContext);
  const dispatch = useAppDispatch();
  const isPreview = useAppSelector(selectIsStudioClientPreview);
  const progress = useAppSelector(selectStudioClientPreviewProgress);

  useEffect(() => {
    const isPreviewLanding =
      query[CLIENT_PREVIEW_PARAM] === CLIENT_PREVIEW_VALUE ||
      query[STUDIO_CLIENT_PREVIEW_PARAM] === CLIENT_PREVIEW_VALUE;
    if (!isPreviewLanding) {
      return;
    }
    dispatch(
      setStudioClientPreview(
        true,
        parseProgress(query[STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM]),
      ),
    );
    const params = new URLSearchParams(history.location.search);
    params.delete(CLIENT_PREVIEW_PARAM);
    params.delete(STUDIO_CLIENT_PREVIEW_PARAM);
    params.delete(STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM);
    history.replace({ search: params.toString() });
  }, [query, dispatch]);

  return { isPreview, progress };
};
